import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../../services/SettingsService';
import {
    getAddressWhitelist, addWhitelistAddress, validateWhitelistAddress, deleteWhitelistAddress
} from '../../services/AddressWhitelistService';
import { processError } from '../../services/ServiceUtils';

/**
 * Camada de SEGURANÇA DE SAQUES (guardrails). Dois blocos:
 *  - Config do guard (kill-switch / dry-run / teto por saque / teto diário) — persiste nas settings
 *    do usuário (colunas withdrawalsEnabled/DryRun/MaxPerTxQuote/DailyLimitQuote).
 *  - Whitelist de endereços — um endereço só é utilizável depois de adicionado E validado (validar
 *    é passo explícito separado). Saque só executa pra endereço validado.
 * fail-closed: com o kill-switch desligado, NENHUM saque executa. Ver backend utils/withdrawGuard.
 */
function WithdrawSecurityPanel() {
    const [config, setConfig] = useState({ withdrawalsEnabled: false, withdrawalsDryRun: true, withdrawMaxPerTxQuote: '', withdrawDailyLimitQuote: '' });
    const [quote, setQuote] = useState('USDT');
    const [whitelist, setWhitelist] = useState([]);
    const [form, setForm] = useState({ coin: '', network: '', address: '', label: '' });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    function loadConfig() {
        getSettings()
            .then(s => {
                setConfig({
                    withdrawalsEnabled: !!s.withdrawalsEnabled,
                    withdrawalsDryRun: s.withdrawalsDryRun === null || s.withdrawalsDryRun === undefined ? true : !!s.withdrawalsDryRun,
                    withdrawMaxPerTxQuote: s.withdrawMaxPerTxQuote ?? '',
                    withdrawDailyLimitQuote: s.withdrawDailyLimitQuote ?? ''
                });
                if (s.preferredQuote) setQuote(s.preferredQuote);
            })
            .catch(err => setMessage({ ok: false, text: processError(err) }));
    }

    function loadWhitelist() {
        getAddressWhitelist()
            .then(rows => setWhitelist(rows || []))
            .catch(err => setMessage({ ok: false, text: processError(err) }));
    }

    useEffect(() => { loadConfig(); loadWhitelist(); }, []);

    function saveConfig() {
        setBusy(true);
        const payload = {
            withdrawalsEnabled: config.withdrawalsEnabled,
            withdrawalsDryRun: config.withdrawalsDryRun,
            withdrawMaxPerTxQuote: config.withdrawMaxPerTxQuote === '' ? null : parseFloat(config.withdrawMaxPerTxQuote),
            withdrawDailyLimitQuote: config.withdrawDailyLimitQuote === '' ? null : parseFloat(config.withdrawDailyLimitQuote)
        };
        updateSettings(payload)
            .then(() => setMessage({ ok: true, text: 'Configuração de segurança de saques salva.' }))
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    function addAddress() {
        if (!form.coin || !form.network || !form.address) {
            setMessage({ ok: false, text: 'Moeda, rede e endereço são obrigatórios.' });
            return;
        }
        setBusy(true);
        addWhitelistAddress(form)
            .then(() => {
                setForm({ coin: '', network: '', address: '', label: '' });
                setMessage({ ok: true, text: 'Endereço adicionado — começa NÃO validado. Confira e valide.' });
                loadWhitelist();
            })
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    function validate(id) {
        setBusy(true);
        validateWhitelistAddress(id)
            .then(() => { setMessage({ ok: true, text: 'Endereço validado — agora pode receber saques.' }); loadWhitelist(); })
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    function remove(id) {
        setBusy(true);
        deleteWhitelistAddress(id)
            .then(() => { setMessage({ ok: true, text: 'Endereço removido.' }); loadWhitelist(); })
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    return (
        <div className='card card-body border-0 shadow mb-4'>
            <h5 className='mb-1'>🔒 Segurança de Saques</h5>
            <p className='small text-muted mb-3'>
                Saque é dinheiro saindo. Com o <strong>interruptor mestre desligado, nenhum saque executa</strong> (fail-closed).
                Um endereço só recebe saque depois de <strong>adicionado e validado</strong>. Em modo simulação, o bot aprova mas não saca de verdade.
            </p>

            {message && (
                <div className={`small mb-3 ${message.ok ? 'text-success' : 'text-danger'}`}>
                    {message.ok ? '✅ ' : '⚠ '}{message.text}
                </div>
            )}

            {/* Config do guard */}
            <div className='border rounded p-3 mb-4'>
                <div className='form-check form-switch mb-2'>
                    <input className='form-check-input' type='checkbox' role='switch' id='wd-enabled'
                        checked={config.withdrawalsEnabled}
                        onChange={e => setConfig({ ...config, withdrawalsEnabled: e.target.checked })} />
                    <label className='form-check-label' htmlFor='wd-enabled'>
                        <strong>Saques habilitados</strong> (interruptor mestre)
                    </label>
                </div>
                <div className='form-check form-switch mb-3'>
                    <input className='form-check-input' type='checkbox' role='switch' id='wd-dryrun'
                        checked={config.withdrawalsDryRun}
                        onChange={e => setConfig({ ...config, withdrawalsDryRun: e.target.checked })} />
                    <label className='form-check-label' htmlFor='wd-dryrun'>
                        Modo simulação (dry-run) — aprova mas <strong>não executa</strong> o saque
                    </label>
                </div>
                <div className='row'>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label small'>Máximo por saque (em {quote})</label>
                        <input type='number' min='0' step='any' className='form-control form-control-sm'
                            value={config.withdrawMaxPerTxQuote}
                            onChange={e => setConfig({ ...config, withdrawMaxPerTxQuote: e.target.value })}
                            placeholder='em branco = sem limite' />
                    </div>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label small'>Teto diário (em {quote})</label>
                        <input type='number' min='0' step='any' className='form-control form-control-sm'
                            value={config.withdrawDailyLimitQuote}
                            onChange={e => setConfig({ ...config, withdrawDailyLimitQuote: e.target.value })}
                            placeholder='em branco = sem limite' />
                    </div>
                </div>
                <button className='btn btn-primary btn-sm' disabled={busy} onClick={saveConfig}>Salvar configuração</button>
            </div>

            {/* Whitelist */}
            <h6 className='mb-2'>Endereços autorizados (whitelist)</h6>
            <div className='table-responsive'>
                <table className='table table-sm table-hover'>
                    <thead>
                        <tr>
                            <th>Moeda</th><th>Rede</th><th>Endereço</th><th>Rótulo</th><th>Status</th><th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {whitelist.length
                            ? whitelist.map(e => (
                                <tr key={e.id}>
                                    <td>{e.coin}</td>
                                    <td>{e.network}</td>
                                    <td className='text-break' style={{ maxWidth: '220px' }}><small>{e.address}</small></td>
                                    <td>{e.label || '-'}</td>
                                    <td>
                                        {e.validated
                                            ? <span className='badge bg-success'>validado</span>
                                            : <span className='badge bg-warning text-dark'>não validado</span>}
                                    </td>
                                    <td>
                                        {!e.validated && (
                                            <button className='btn btn-outline-success btn-sm me-1' disabled={busy} onClick={() => validate(e.id)}>Validar</button>
                                        )}
                                        <button className='btn btn-outline-danger btn-sm' disabled={busy} onClick={() => remove(e.id)}>Remover</button>
                                    </td>
                                </tr>
                            ))
                            : <tr><td colSpan='6' className='text-muted small'>Nenhum endereço na whitelist.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Adicionar endereço */}
            <div className='border rounded p-3 mt-2'>
                <div className='row'>
                    <div className='col-md-2 mb-2'>
                        <label className='form-label small'>Moeda</label>
                        <input type='text' className='form-control form-control-sm' value={form.coin}
                            onChange={e => setForm({ ...form, coin: e.target.value.toUpperCase() })} placeholder='USDT' />
                    </div>
                    <div className='col-md-2 mb-2'>
                        <label className='form-label small'>Rede</label>
                        <input type='text' className='form-control form-control-sm' value={form.network}
                            onChange={e => setForm({ ...form, network: e.target.value.toUpperCase() })} placeholder='TRX' />
                    </div>
                    <div className='col-md-5 mb-2'>
                        <label className='form-label small'>Endereço</label>
                        <input type='text' className='form-control form-control-sm' value={form.address}
                            onChange={e => setForm({ ...form, address: e.target.value })} placeholder='endereço de destino' />
                    </div>
                    <div className='col-md-3 mb-2'>
                        <label className='form-label small'>Rótulo (opcional)</label>
                        <input type='text' className='form-control form-control-sm' value={form.label}
                            onChange={e => setForm({ ...form, label: e.target.value })} placeholder='ex: minha carteira fria' />
                    </div>
                </div>
                <button className='btn btn-outline-primary btn-sm' disabled={busy} onClick={addAddress}>Adicionar endereço</button>
            </div>
        </div>
    );
}

export default WithdrawSecurityPanel;
